/* eslint-disable */
import {
  Box,
  makeStyles,
  Typography,
  Fade,
  Grow,
  IconButton,
  Snackbar,
  useMediaQuery,
} from '@material-ui/core';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import RoundedButton from 'components/Shared/RoundedButton';
import LinkIcon from '@material-ui/icons/Link';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import LaunchIcon from '@material-ui/icons/Launch';
import CheckIcon from '@material-ui/icons/Check';
import { useDispatch, useSelector } from 'react-redux';
import copy from 'copy-to-clipboard';
import { enqueueSnackbar, SNACKBAR_VARIANTS } from '../../../redux_store';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: theme.shape.borderRadius * 2,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      borderRadius: theme.shape.borderRadius * 2,
      zIndex: -1,
    },
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6, 4),
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 2),
      justifyContent: 'flex-start',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 1),
    },
  },
  successCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
    minWidth: 320,
    maxWidth: 600,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4),
      minWidth: 280,
      maxWidth: 460,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 3),
      minWidth: 260,
      maxWidth: 320,
      margin: theme.spacing(0, 1),
    },
  },
  iconContainer: {
    position: 'relative',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  iconBox: {
    width: 88,
    height: 88,
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${theme.colors.lightBlue}, ${theme.colors.lightBlue3})`,
    boxShadow: `0 8px 24px ${theme.colors.lightBlue}40, 0 2px 8px rgba(0,0,0,0.1)`,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: 72,
      height: 72,
    },
    [theme.breakpoints.down('xs')]: {
      width: 64,
      height: 64,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -8,
      left: -8,
      right: -8,
      bottom: -8,
      borderRadius: '50%',
      border: `2px solid ${theme.colors.lightBlue}20`,
      animation: '$pulse 2s infinite',
      [theme.breakpoints.down('xs')]: {
        top: -6,
        left: -6,
        right: -6,
        bottom: -6,
      },
    },
  },
  linkIcon: {
    width: 40,
    height: 40,
    color: theme.palette.common.white,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    [theme.breakpoints.down('sm')]: {
      width: 32,
      height: 32,
    },
    [theme.breakpoints.down('xs')]: {
      width: 28,
      height: 28,
    },
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
    background: `linear-gradient(135deg, ${theme.colors.lightBlue}, ${theme.colors.blue5})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
      letterSpacing: '-0.3px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
      letterSpacing: '0px',
    },
  },
  subtitle: {
    fontSize: 16,
    fontWeight: 400,
    textAlign: 'center',
    color: theme.colors.gray4,
    marginBottom: theme.spacing(4),
    lineHeight: 1.5,
    maxWidth: 500,
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      marginBottom: theme.spacing(3),
      maxWidth: 350,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 13,
      marginBottom: theme.spacing(2.5),
      maxWidth: 280,
    },
  },
  linkSection: {
    width: '100%',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  linkContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: `${theme.colors.lightBlue}08`,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.colors.lightBlue}20`,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: `${theme.colors.lightBlue}12`,
      borderColor: `${theme.colors.lightBlue}30`,
    },
  },
  linkText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'monospace',
    color: theme.colors.blue2,
    wordBreak: 'break-all',
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },
  copyButton: {
    padding: theme.spacing(0.5),
    color: theme.colors.lightBlue,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: `${theme.colors.lightBlue}15`,
      color: theme.colors.blue5,
    },
  },
  copyIcon: {
    width: 20,
    height: 20,
    [theme.breakpoints.down('xs')]: {
      width: 18,
      height: 18,
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
  },
  openButton: {
    flex: 1,
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.shape.borderRadius,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.common.white,
    background: `linear-gradient(135deg, ${theme.colors.lightBlue}, ${theme.colors.lightBlue3})`,
    border: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    gap: theme.spacing(1),
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.colors.lightBlue}, ${theme.colors.blue5})`,
      boxShadow: 'none',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
  },
  actionsContainer: {
    padding: theme.spacing(2, 3, 0, 3),
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    flexShrink: 0,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 0, 2),
    },
  },
  closeButton: {
    width: '100%',
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.common.white,
    background: `linear-gradient(135deg, ${theme.colors.lightBlue}, ${theme.colors.lightBlue3})`,
    border: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.colors.lightBlue}, ${theme.colors.blue5})`,
      boxShadow: 'none',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.5,
      transform: 'scale(1.05)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  fadeInUp: {
    animation: '$fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  createButton: {
    textTransform: 'uppercase',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 2),
    letterSpacing: 2,
    fontWeight: 'bold',
    fontSize: 13,
    maxHeight: 40,
    minWidth: 120,
  },
}));

const LinkGenerated = ({ onCloseModal, link }) => {
  const classes = useStyles();
  const isTallScreen = useMediaQuery('(min-height: 850px)');
  const {
    en: { titles, btn },
  } = useSelector(state => state.i18n);
  const [showContent, setShowContent] = useState(false);
  const [copied, setCopied] = useState(false);
  const dispatch = useDispatch();
  const {
    en: { notifications },
  } = useSelector(state => state.i18n);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const copyToClipBoard = async () => {
    try {
      await copy(link);
      dispatch(
        enqueueSnackbar({
          message: notifications.dataCopied,
          options: {
            variant: SNACKBAR_VARIANTS.success,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          },
        })
      );
    } catch (err) {
      enqueueSnackbar({
        message: notifications.dataNotCopied,
        options: {
          variant: SNACKBAR_VARIANTS.error,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        },
      });
    }
  };

  const openInNewTab = () => {
    window.open(link, '_blank');
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.contentArea}>
        <Fade in={showContent} timeout={600}>
          <Box className={`${classes.successCard} ${classes.fadeInUp}`}>
            {isTallScreen && (
              <Grow in={showContent} timeout={800}>
                <Box className={classes.iconContainer}>
                  <Box className={classes.iconBox}>
                    <LinkIcon className={classes.linkIcon} />
                  </Box>
                </Box>
              </Grow>
            )}

            <Typography className={classes.title}>Assessment Link Generated</Typography>

            <Typography className={classes.subtitle}>
              Your assessment link has been successfully generated. Share this secure link with your
              patient to begin their assessment.
            </Typography>

            <Box className={classes.linkSection}>
              <Box className={classes.linkContainer}>
                <Typography className={classes.linkText}>{link}</Typography>
                <IconButton onClick={copyToClipBoard} className={classes.copyButton} size="small">
                  {copied ? (
                    <CheckIcon className={classes.copyIcon} />
                  ) : (
                    <FileCopyOutlinedIcon className={classes.copyIcon} />
                  )}
                </IconButton>
              </Box>

              <Box className={classes.actionButtons}>
                <RoundedButton
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={openInNewTab}
                  className={classes.openButton}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      justifyContent: 'center',
                    }}
                  >
                    <LaunchIcon style={{ width: 18, height: 18 }} />
                    {'Open Assessment'}
                  </div>
                </RoundedButton>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>

      <Box className={classes.actionsContainer}>
        <RoundedButton
          variant="contained"
          color="primary"
          size="small"
          onClick={onCloseModal}
          className={classes.createButton}
        >
          {btn.close}
        </RoundedButton>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

LinkGenerated.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  link: PropTypes.string.isRequired,
};

export default LinkGenerated;
